import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { describeDevice } from './device';
import { ConfirmDialog, Panel, SessionRow } from './parts';
import { useConfirm } from './useConfirm';
import { useEndMemberSessions, useEndTeamSession, useTeamSessions } from './queries';
import type { TeamMemberSessions, TeamSession } from './types';

type Target =
  | { kind: 'session'; member: TeamMemberSessions; session: TeamSession }
  | { kind: 'member'; member: TeamMemberSessions };

/**
 * The lab owner's view of where their team is signed in - everyone on the
 * licence group except the owner, whose own devices are in the card beside.
 *
 * Rendered for owners only; the server answers 403 to anybody else anyway.
 */
export function TeamSessionsCard() {
  const team = useTeamSessions(true);
  const endOne = useEndTeamSession();
  const endAll = useEndMemberSessions();
  const confirm = useConfirm<Target>();

  const now = team.dataUpdatedAt;
  const members = team.data ?? [];
  const target = confirm.target;

  let body;
  if (team.isPending) {
    body = (
      <div className="space-y-2 px-[26px] py-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-11 w-full bg-muted" />
        ))}
      </div>
    );
  } else if (team.isError) {
    body = (
      <p className="px-[26px] py-6 text-xs text-destructive">
        Your team&apos;s sessions could not be loaded. Try again in a moment.
      </p>
    );
  } else if (members.length === 0) {
    body = (
      <p className="px-[26px] py-6 text-xs text-[#8A94A6]">Nobody else is on your licence yet.</p>
    );
  } else {
    body = (
      <div className="divide-y divide-[#F1F4F8]">
        {members.map((member) => (
          <div key={member.portalUserId}>
            <div className="flex flex-wrap items-center justify-between gap-2 bg-[#F7F9FC] px-[26px] py-2.5">
              <div className="min-w-0">
                <span className="text-[12.5px] font-semibold">{member.name}</span>
                {member.jobTitle && (
                  <span className="text-[11.5px] text-[#8A94A6]"> · {member.jobTitle}</span>
                )}
                <span className="text-[11.5px] text-[#8A94A6]">
                  {' · '}
                  {member.sessions.length === 0
                    ? 'not signed in'
                    : `${String(member.sessions.length)} ${
                        member.sessions.length === 1 ? 'device' : 'devices'
                      }`}
                </span>
              </div>
              {member.sessions.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    confirm.ask({ kind: 'member', member });
                  }}
                >
                  Sign out everywhere
                </Button>
              )}
            </div>
            {member.sessions.length > 0 && (
              <ul className="divide-y divide-[#F1F4F8]">
                {member.sessions.map((session) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    now={now}
                    action={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          confirm.ask({ kind: 'session', member, session });
                        }}
                      >
                        Sign out
                      </Button>
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Panel
      title="Team sessions"
      description="Where the people on your licence are signed in, in this portal and the NuDent app."
    >
      {body}

      <ConfirmDialog
        open={target !== null}
        onOpenChange={(open) => {
          if (!open) confirm.close();
        }}
        title={
          target?.kind === 'member'
            ? `Sign ${target.member.name} out everywhere?`
            : `Sign ${target?.member.name ?? ''} out of this device?`
        }
        description={
          target?.kind === 'member'
            ? `Every browser and NuDent app ${target.member.name} is signed in on will need their password to sign in again.`
            : target
              ? `${describeDevice(target.session).label} will need ${target.member.name}'s password to sign in again.`
              : ''
        }
        confirmLabel={target?.kind === 'member' ? 'Sign out everywhere' : 'Sign out'}
        pending={endOne.isPending || endAll.isPending}
        onConfirm={() => {
          if (!target) return;
          const done = {
            onSuccess: () => {
              toast.success(`${target.member.name} has been signed out.`);
              confirm.close();
            },
            onError: () => toast.error('Could not sign them out. Try again.'),
          };
          if (target.kind === 'member') endAll.mutate(target.member.portalUserId, done);
          else endOne.mutate(target.session.id, done);
        }}
      />
    </Panel>
  );
}
